/**
 * useWatchlist Hook - Keyword-group tracking over the analytics date range
 *
 * Loads watch groups (localStorage), fetches transactions for the selected
 * range PLUS the equal-length period right before it (trend baseline), and
 * computes per-group stats via the watchlist service. Re-computes on
 * transaction changes (dataEvents) and on group edits.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { transactionService } from '@services/transactions';
import { dataEvents } from '@services/dataEvents';
import {
  loadWatchGroups,
  saveWatchGroups,
  computeWatchlistStats,
  type WatchGroup,
  type WatchGroupStats,
} from '@services/watchlist';

export interface UseWatchlistResult {
  groups: WatchGroup[];
  stats: WatchGroupStats[];
  loading: boolean;
  /** Persist a full replacement group list (component owns the edits) */
  updateGroups: (groups: WatchGroup[]) => void;
  /** Most active group with matches this period, for the insights engine */
  topStats: WatchGroupStats | null;
}

export function useWatchlist(range: { start: Date; end: Date }): UseWatchlistResult {
  const [groups, setGroups] = useState<WatchGroup[]>(() => loadWatchGroups());
  const [stats, setStats] = useState<WatchGroupStats[]>([]);
  const [loading, setLoading] = useState(true);
  // After the first computation, later recomputes (group edits, new txns)
  // keep showing the previous stats instead of flashing a loading state.
  const hasLoadedRef = useRef(false);

  const rangeStartMs = range.start.getTime();
  const rangeEndMs = range.end.getTime();

  const recompute = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const start = new Date(rangeStartMs);
      const end = new Date(rangeEndMs);
      const durationMs = Math.max(86400000, rangeEndMs - rangeStartMs);
      const rangeDays = durationMs / 86400000;

      // "All Time" ranges (start <= year 2000) have no meaningful baseline -
      // skip the extra query instead of scanning half the database twice.
      const isAllTime = start.getFullYear() <= 2000;
      const prevStart = new Date(rangeStartMs - durationMs);
      const prevEnd = new Date(rangeStartMs - 1);

      const [current, previous] = await Promise.all([
        transactionService.getByDateRange(start, end),
        isAllTime
          ? Promise.resolve([])
          : transactionService.getByDateRange(prevStart, prevEnd),
      ]);

      // All-Time has no "previous period" - stats carry hasBaseline=false so
      // the UI never renders a misleading "New this period" tag there.
      setStats(computeWatchlistStats(current, previous, groups, rangeDays, !isAllTime));
    } catch (err) {
      console.error('[useWatchlist] Error:', err);
      setStats([]);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [groups, rangeStartMs, rangeEndMs]);

  // Debounced: group edits save on every keystroke (name typing, term adds),
  // and re-matching 2000+ transactions per keypress is pointless work. The
  // debounce also batches rapid range/group changes into one recompute.
  useEffect(() => {
    const timer = setTimeout(recompute, 250);
    return () => clearTimeout(timer);
  }, [recompute]);

  useEffect(() => {
    return dataEvents.on('transactions', recompute);
  }, [recompute]);

  const updateGroups = useCallback((next: WatchGroup[]) => {
    saveWatchGroups(next);
    setGroups(next);
  }, []);

  // Insight feed: top group with matches, excluding capital-move groups -
  // the insight text says "spending", which is exactly what those are NOT.
  const topStats = stats.find((s) => s.count > 0 && !s.group.excludeFromSpending) || null;

  return { groups, stats, loading, updateGroups, topStats };
}
